import React, { useState, useMemo, useEffect } from 'react';
import { Plus, Search, Grid, List, Tag, UtensilsCrossed, RefreshCw, X, Upload, Download, Activity, Clock, CheckCircle2, AlertCircle, IndianRupee } from 'lucide-react';
import toast from 'react-hot-toast';
import MenuList from './MenuList';
import MenuForm from './MenuForm';
import BulkPriceEditor from './BulkPriceEditor';
import CategoryView from './CategoryView';
import { ConfirmDialog, AlertDialog } from './UIComponents';
import { itemService } from '../../../services/menuitemService';
import { categoryService } from '../../../services/categoryService';
import KitchenStationManager from './KitchenStationManager';
import { useAuth } from '../../../context/AuthContext';
import { P } from '@/config/permissionCodes';
import { extractApiMessage, isDuplicateError } from '@/utils/apiError';

export default function MenuManagement() {
  const { hasPermission } = useAuth();

  // ── Permissions ───────────────────────────────────────────────
  const canViewScreen    = hasPermission(P.BACK_OFFICE.MENU.SCREEN);
  const canAddItem       = hasPermission(P.BACK_OFFICE.MENU.ITEMS.ADD);
  const canEditItem      = hasPermission(P.BACK_OFFICE.MENU.ITEMS.EDIT);
  const canDeleteItem    = hasPermission(P.BACK_OFFICE.MENU.ITEMS.DELETE);
  const canManageCats    = hasPermission(P.BACK_OFFICE.MENU.CATEGORIES.ADD)
                        || hasPermission(P.BACK_OFFICE.MENU.CATEGORIES.EDIT)
                        || hasPermission(P.BACK_OFFICE.MENU.CATEGORIES.DELETE);
  const canManageKitchen = hasPermission(P.BACK_OFFICE.MENU.KITCHEN.ADD)
                        || hasPermission(P.BACK_OFFICE.MENU.KITCHEN.EDIT)
                        || hasPermission(P.BACK_OFFICE.MENU.KITCHEN.DELETE);

  if (!canViewScreen) {
    return (
      <div className="flex items-center justify-center h-full py-24">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Access Denied</h3>
          <p className="text-sm text-slate-500">You don't have permission to view Menu Management.</p>
        </div>
      </div>
    );
  }

  // ── State ─────────────────────────────────────────────────────
  const [menuItems, setMenuItems]       = useState([]);
  const [categories, setCategories]     = useState([]);
  const [loading, setLoading]           = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const [viewMode, setViewMode]                     = useState('grid');
  const [selectedCategory, setSelectedCategory]     = useState('all');
  const [showKitchenStationManager, setShowKitchenStationManager] = useState(false);
  const [searchQuery, setSearchQuery]               = useState('');
  const [statusFilter, setStatusFilter]             = useState('all');
  const [itemTypeFilter, setItemTypeFilter]         = useState('all');
  const [sortBy, setSortBy]                         = useState('name-asc');

  const [showMenuForm, setShowMenuForm]             = useState(false);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [showBulkUploadDialog, setShowBulkUploadDialog] = useState(false);
  const [showBulkPriceEditor, setShowBulkPriceEditor] = useState(false);
  const [bulkFile, setBulkFile]                       = useState(null);
  const [isUploading, setIsUploading]                 = useState(false);
  const [bulkVersion, setBulkVersion]                 = useState('V1');
  const [editingItem, setEditingItem]               = useState(null);
  const [selectedItems, setSelectedItems]           = useState([]);
  const [selectedBranchId, setSelectedBranchId]     = useState(null);
  const [bulkUploadResult, setBulkUploadResult]     = useState(null);

  // Field-level error handed back to MenuForm (e.g. duplicate name from the API),
  // so the form can highlight the field instead of showing a dead-end dialog.
  const [formServerError, setFormServerError]       = useState(null);

  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, type: '', data: null });
  const [alertDialog, setAlertDialog]     = useState({ isOpen: false, title: '', message: '', type: 'error' });

  // ── Derived ───────────────────────────────────────────────────
  const branches = useMemo(() => {
    const map = new Map();
    menuItems.forEach((item) => {
      if (item.branchId && !map.has(item.branchId))
        map.set(item.branchId, { id: item.branchId, name: item.branchName });
    });
    const list = Array.from(map.values());
    return list.length > 1 ? list : [];
  }, [menuItems]);

  useEffect(() => {
    if (branches.length > 1 && !selectedBranchId) setSelectedBranchId(branches[0].id);
    if (branches.length <= 1) setSelectedBranchId(null);
  }, [branches]);

  useEffect(() => {
    fetchMenuItems();
    fetchCategories();
  }, []);

  // ── Data fetching ─────────────────────────────────────────────
  const fetchMenuItems = async () => {
    setLoading(true);
    try {
      const data = await itemService.getAll();
      setMenuItems(data || []);
      if (!isInitialLoad) showToast('Menu items refreshed successfully', 'success');
      else setIsInitialLoad(false);
    } catch (error) {
      console.error('Failed to load menu items:', error);
      if (!isInitialLoad) showToast('Failed to refresh menu items', 'error');
      showAlert('Error', 'Unable to fetch menu items. Please check your connection.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const data = await categoryService.getAll();
      setCategories(data || []);
    } catch (error) {
      console.error('Failed to load categories:', error);
      showAlert('Error', 'Unable to fetch categories. Please try again later.', 'error');
    }
  };

  // Items the form should treat as name-taken. Scoped to the selected branch because
  // the server's duplicate check runs under the tenantFilter (branch_id = :branchId),
  // so the same item name IS allowed in two different branches. Deliberately not
  // reusing filteredItems — that also applies search/category/status filters, which
  // would let a hidden row slip through as a false negative.
  const itemsInScope = useMemo(
    () => (selectedBranchId ? menuItems.filter((i) => i.branchId === selectedBranchId) : menuItems),
    [menuItems, selectedBranchId]
  );

  // ── Filtered items ────────────────────────────────────────────
  const filteredItems = useMemo(() => {
    let items = [...menuItems];
    if (selectedBranchId) items = items.filter((i) => i.branchId === selectedBranchId);
    if (searchQuery.trim()) {
  const q = searchQuery.toLowerCase();
  items = items.filter(
    (i) =>
      i.name.toLowerCase().includes(q) ||
      i.description?.toLowerCase().includes(q) ||
      i.itemCode?.toLowerCase().includes(q) ||
      i.barcode?.toLowerCase().includes(q)
      
  );
}
    if (selectedCategory !== 'all')
      items = items.filter((i) => String(i.categoryId) === String(selectedCategory));
    if (statusFilter === 'active')   items = items.filter((i) => i.isActive);
    if (statusFilter === 'inactive') items = items.filter((i) => !i.isActive);
    if (itemTypeFilter === 'menu')    items = items.filter((i) => !i.markedAsToppings);
    if (itemTypeFilter === 'topping') items = items.filter((i) => i.markedAsToppings);
    if (sortBy === 'name-asc')   items.sort((a, b) => a.name.localeCompare(b.name));
    if (sortBy === 'name-desc')  items.sort((a, b) => b.name.localeCompare(a.name));
    if (sortBy === 'price-asc')  items.sort((a, b) => a.basePrice - b.basePrice);
    if (sortBy === 'price-desc') items.sort((a, b) => b.basePrice - a.basePrice);
    return items;
  }, [menuItems, searchQuery, selectedCategory, statusFilter, sortBy, selectedBranchId, itemTypeFilter]);

  const branchCategories = useMemo(
    () => (!selectedBranchId ? categories : categories.filter((c) => c.branchId === selectedBranchId)),
    [categories, selectedBranchId]
  );

  // ── Helpers ───────────────────────────────────────────────────
  const showToast = (message, type = 'success') => {
    if (type === 'success') toast.success(message);
    else if (type === 'error') toast.error(message);
    else toast(message);
  };
  const showAlert = (title, message, type = 'error') =>
    setAlertDialog({ isOpen: true, title, message, type });


  const validateItem = (item) => {
    const errors = {};
    if (!item.name?.trim()) errors.name = 'Name is required';
    if (!item.categoryId) errors.categoryId = 'Category is required';
    if (item.basePrice === '' || item.basePrice === null || isNaN(item.basePrice))
      errors.basePrice = 'Valid base price is required';
    if (!item.hsn?.trim()) errors.hsn = 'HSN code is required';
    if (item.variants?.length > 0) {
      const variantErrors = [];
      item.variants.forEach((v) => {
        const vErr = {};
        if (!v.variantName && !v.name) vErr.variantName = 'Variant name is required';
        if (v.variantPrice === '' || v.variantPrice === null || isNaN(v.variantPrice))
          vErr.variantPrice = 'Variant price is required';
        if (Object.keys(vErr).length > 0) variantErrors.push(vErr);
      });
      if (variantErrors.length > 0) errors.variantErrors = variantErrors;
    }
    return { isValid: Object.keys(errors).length === 0, errors };
  };

  // ── Save item ─────────────────────────────────────────────────
  const handleSaveItem = async (itemData) => {
    // Permission guard
    if (editingItem && !canEditItem) return;
    if (!editingItem && !canAddItem) return;

    // Drop any stale field error from the previous attempt.
    setFormServerError(null);

    const { isValid, errors } = validateItem(itemData);
    if (!isValid) {
      const formatted = Object.entries(errors)
        .map(([key, val]) => {
          if (Array.isArray(val))
            return `${key}:\n  ${val.map((v) =>
              typeof v === 'object'
                ? Object.entries(v).map(([k, msg]) => `• ${k}: ${msg}`).join('\n  ')
                : `• ${v}`
            ).join('\n  ')}`;
          return `${key}: ${val}`;
        })
        .join('\n\n');
      showAlert('Validation Error', 'Please fix the following errors:\n\n' + formatted, 'error');
      return;
    }

    try {
      const payload = {
        id: editingItem?.id || 0,
        name: itemData.name,
        description: itemData.description || '',
        categoryId: parseInt(itemData.categoryId) || 0,
        categoryName: categories.find((c) => c.id === parseInt(itemData.categoryId))?.name || '',
        kitchenStationId: itemData.kitchenStationId ? Number(itemData.kitchenStationId) : null,
        kitchenStationName: itemData.kitchenStationName || null,
        basePrice: parseFloat(itemData.basePrice) || 0,
        isAvailable: itemData.isAvailable ?? true,
        isActive: itemData.isActive ?? true,
        preparationTime: itemData.preparationTime || 0,
        isVeg: itemData.isVeg ?? true,
        isFavourite: itemData.isFavourite ?? false,
        isCustomizable: itemData.isCustomizable ?? false,
        taxId: itemData.taxId ?? null,
        inclusiveTaxRate: itemData.inclusiveTaxRate ?? true,
        shortcutKey: itemData.shortcutKey || 0,
        imageUrl: itemData.imageUrl !== undefined ? itemData.imageUrl : null,
        hsn: itemData.hsn || null,
        itemCode: itemData.itemCode || null,
        barcode: itemData.barcode || null,
        takeawayPrice: itemData.takeawayPrice ?? null,
        deliveryPrice: itemData.deliveryPrice ?? null,
        isOpenItem: itemData.isOpenItem ?? false,
        isNameEditable: itemData.isNameEditable ?? false,
        isFinishedItem: itemData.isFinishedItem ?? false,
        markedAsToppings: itemData.markedAsToppings ?? false,  
        variants: (itemData.variants || []).map((v) => {
          const variant = {
            menuItemId: editingItem?.id || 0,
            variantName: v.variantName || v.name || '',
            variantPrice: parseFloat(v.variantPrice || v.price || 0),
            isAvailable: v.isAvailable ?? true,
            takeawayPrice: v.takeawayPrice ?? null,
            deliveryPrice: v.deliveryPrice ?? null,
          };
          if (v.id) variant.id = v.id;
          return variant;
        }),
        deletedVariantIds: itemData.deletedVariantIds || [],
      };

      if (editingItem) {
        await itemService.update(editingItem.id, payload);
        showToast('Menu item updated');
      } else {
        await itemService.create(payload);
        showToast('Menu item added');
      }

      await fetchMenuItems();
      setShowMenuForm(false);
      setEditingItem(null);
    } catch (error) {
      console.error('Save failed:', error);
      const message = extractApiMessage(error, 'Failed to save menu item. Please try again.');

      // A duplicate name is a field problem, not a system failure: keep the form open,
      // point at the offending field and let the user fix it in place.
      if (isDuplicateError(error, message)) {
        setFormServerError({
          field: 'name',
          message: `A menu item named "${itemData.name}" already exists. Please choose a different name.`,
        });
        showToast('That menu item name is already taken', 'error');
        return;
      }

      showAlert('Could not save menu item', message, 'error');
    }
  };

  // ── Delete / bulk ops ─────────────────────────────────────────
  const handleDeleteItem    = (id) => setConfirmDialog({ isOpen: true, type: 'delete', data: id });
  const handleBulkDelete    = ()   => setConfirmDialog({ isOpen: true, type: 'bulkDelete', data: selectedItems });
  const handleEditItem      = (item) => { setEditingItem(item); setShowMenuForm(true); };

  const confirmDelete = async () => {
    if (!canDeleteItem) return;
    try {
      await itemService.remove(confirmDialog.data);
      await fetchMenuItems();
      showToast('Item deleted');
    } catch {
      showAlert('Error', 'Failed to delete item.', 'error');
    }
  };

  const confirmBulkDelete = async () => {
    if (!canDeleteItem) return;
    try {
      await Promise.all(selectedItems.map((id) => itemService.remove(id)));
      await fetchMenuItems();
      setSelectedItems([]);
      showToast(`${selectedItems.length} items deleted`);
    } catch {
      showAlert('Error', 'Failed to delete some items.', 'error');
    }
  };

  const handleBulkStatusChange = async (isActive) => {
    if (!canEditItem) return;
    try {
      await Promise.all(
        selectedItems.map((id) => {
          const item = menuItems.find((i) => i.id === id);
          return item ? itemService.update(id, { ...item, isActive }) : null;
        }).filter(Boolean)
      );
      await fetchMenuItems();
      setSelectedItems([]);
      showToast(`Items ${isActive ? 'activated' : 'deactivated'}`);
    } catch (error) {
      console.error('Status update failed:', error);
      showAlert('Error', 'Failed to update item statuses.', 'error');
    }
  };

  const handleCategoriesUpdate = async (updated) => {
    setCategories(updated);
    showToast('Categories updated');
    await fetchCategories();
  };

  const handleKitchenStationsSave = (stations) => {
    console.log('Saved kitchen stations:', stations);
    showToast('Kitchen stations updated', 'success');
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await itemService.downloadTemplate(bulkVersion);
      const blob = response instanceof Blob ? response : new Blob([response.data ?? response], { type: 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Bulk_Upload_Menu_Template_${bulkVersion}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showToast('Template downloaded successfully');
    } catch (error) {
      console.error('Failed to download template:', error);
      showAlert('Error', 'Failed to download template. Please try again.', 'error');
    }
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) {
      showAlert('Validation Error', 'Please select a file to upload.', 'error');
      return;
    }
    setIsUploading(true);
    try {
      const response = await itemService.uploadBulkMenu(bulkFile, bulkVersion);
      const resData = response?.data || response;
      
      let details = "";
      if (resData) {
        details = `Message: ${resData.message || 'Processed successfully'}\n` +
                  `Time Taken: ${resData.timeTakenMs || 0}ms\n` +
                  `Stations: ${resData.stationsProcessed || 0}\n` +
                  `Categories: ${resData.categoriesProcessed || 0}\n` +
                  `Items: ${resData.itemsProcessed || 0}\n` +
                  `Variants: ${resData.variantsProcessed || 0}`;
        
        if (resData.rowErrors && resData.rowErrors.length > 0) {
          details += "\n\nErrors:\n" + resData.rowErrors.map(e => `Row ${e.rowNumber || '?'}: ${e.errorMessage}`).join("\n");
        }
      }
      
      setBulkUploadResult(resData);
      setShowBulkUploadDialog(false);
      setBulkFile(null);
      await fetchMenuItems();
      await fetchCategories();
    } catch (error) {
      console.error('Bulk upload failed:', error);
      let message = 'Failed to upload bulk menu.';
      if (error.response?.data) {
        const data = error.response.data;
        if (typeof data === 'string') message = data;
        else if (data.detail) message = data.detail;
        else if (Array.isArray(data)) message = data.map(e => `Row ${e.rowNumber || '?'}: ${e.errorMessage}`).join("\n");
        else if (typeof data === 'object') {
          if (data.totalRecords !== undefined) {
            message = `Total Records: ${data.totalRecords || 0}\nSuccess: ${data.successCount || 0}\nFailed: ${data.failedCount || 0}`;
            if (data.errors && data.errors.length > 0) {
              message += "\n\nErrors:\n" + data.errors.map(e => `Row ${e.rowNumber || '?'}: ${e.errorMessage}`).join("\n");
            }
          } else {
            message = Object.entries(data).map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : v}`).join('\n');
          }
        }
      }
      showAlert('Upload Failed', message, 'error');
    } finally {
      setIsUploading(false);
    }
  };

  const confirmConfig = (() => {
    switch (confirmDialog.type) {
      case 'delete':
        return { title: 'Delete Menu Item', message: 'Are you sure you want to delete this item?', confirmText: 'Delete', type: 'danger', onConfirm: confirmDelete };
      case 'bulkDelete':
        return { title: 'Delete Multiple Items', message: `Are you sure you want to delete ${confirmDialog.data?.length} items?`, confirmText: 'Delete All', type: 'danger', onConfirm: confirmBulkDelete };
      default:
        return null;
    }
  })();

  // ── Render ────────────────────────────────────────────────────
  return (
    <div className="min-h-full bg-gray-50 px-4 py-6">
      <div className="space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Menu Management</h1>
            <p className="text-sm text-gray-600 mt-0.5">Manage your menu items</p>
          </div>
          <div className="flex gap-2">
            {/* Kitchen Stations button — only if any kitchen permission */}
            {canManageKitchen && (
              <button
                onClick={() => setShowKitchenStationManager(true)}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2 text-sm font-medium"
              >
                <UtensilsCrossed size={16} />
                <span>Kitchen Stations</span>
              </button>
            )}
            {/* Categories button — only if any category permission */}
            {canManageCats && (
              <button
                onClick={() => setShowCategoryManager(true)}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 flex items-center gap-2 text-sm font-medium"
              >
                <Tag size={16} />
                <span>Categories</span>
              </button>
            )}
            {/* Bulk Price Update button */}
            <button
              onClick={() => setShowBulkPriceEditor(true)}
              className="px-4 py-2 bg-amber-50 border border-amber-300 text-amber-700 rounded-lg hover:bg-amber-100 transition-colors flex items-center gap-2 text-sm font-medium"
            >
              <IndianRupee size={16} />
              <span>Bulk Price Update</span>
            </button>
            {/* Add Item & Bulk Upload buttons */}
            {canAddItem && (
              <>
                <button
                  onClick={() => setShowBulkUploadDialog(true)}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2 text-sm font-medium"
                >
                  <Upload size={16} />
                  <span>Bulk Upload</span>
                </button>
                <button
                  onClick={() => { setEditingItem(null); setShowMenuForm(true); }}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center gap-2 text-sm font-medium"
                >
                  <Plus size={16} />
                  <span>Add Item</span>
                </button>
              </>
            )}
                  {/* Bulk Price Editor Modal */}
                  {showBulkPriceEditor && (
                    <BulkPriceEditor
                      categories={categories}
                      onClose={() => setShowBulkPriceEditor(false)}
                      onSaveSuccess={fetchMenuItems}
                    />
                  )}
          </div>
        </div>

        {/* Branch Tabs */}
        {branches.length > 1 && (
          <div className="flex gap-1 bg-white border border-gray-200 rounded-lg p-1">
            {branches.map((b) => (
              <button
                key={b.id}
                onClick={() => { setSelectedBranchId(b.id); setSelectedCategory('all'); setSelectedItems([]); }}
                className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                  selectedBranchId === b.id ? 'bg-teal-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {b.name}
              </button>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <div className="flex flex-wrap gap-3">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
  type="text"
  placeholder="Search menu items..."
  value={searchQuery}
  onChange={(e) => setSearchQuery(e.target.value)}
  className="w-full pl-10 pr-4 py-2 border border-slate-300 hover:border-slate-400 focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-600 text-sm rounded-lg"
/>
            </div>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm">
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <select value={itemTypeFilter} onChange={(e) => setItemTypeFilter(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm">
  <option value="all">All Items</option>
  <option value="menu">Menu Items</option>
  <option value="topping">Toppings</option>
</select>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm">
              <option value="name-asc">Name A–Z</option>
              <option value="name-desc">Name Z–A</option>
              <option value="price-asc">Price Low–High</option>
              <option value="price-desc">Price High–Low</option>
            </select>
            <button onClick={fetchMenuItems} disabled={loading} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 text-gray-600 hover:bg-gray-100 border border-gray-200 text-sm font-medium transition-all disabled:opacity-50">
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </button>
            <div className="flex border border-gray-300 rounded-lg overflow-hidden">
              <button onClick={() => setViewMode('grid')} className={`p-2 ${viewMode === 'grid' ? 'bg-teal-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}><Grid size={18} /></button>
              <button onClick={() => setViewMode('list')} className={`p-2 border-l border-gray-300 ${viewMode === 'list' ? 'bg-teal-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}><List size={18} /></button>
            </div>
          </div>

          {/* Bulk action bar — only show actions the user can perform */}
          {selectedItems.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-200 flex items-center justify-between">
              <span className="text-sm text-gray-700 font-medium">{selectedItems.length} selected</span>
              <div className="flex gap-2">
                {canEditItem && (
                  <>
                    <button onClick={() => handleBulkStatusChange(true)} className="px-3 py-1.5 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-700">Activate</button>
                    <button onClick={() => handleBulkStatusChange(false)} className="px-3 py-1.5 bg-gray-600 text-white rounded text-xs font-medium hover:bg-gray-700">Deactivate</button>
                  </>
                )}
                {canDeleteItem && (
                  <button onClick={handleBulkDelete} className="px-3 py-1.5 bg-red-600 text-white rounded text-xs font-medium hover:bg-red-700">Delete</button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Menu List */}
        {loading ? (
          <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
            <div className="flex flex-col items-center justify-center gap-4">
              <RefreshCw size={48} className="animate-spin text-teal-600" />
              <h3 className="text-lg font-semibold text-gray-900">Refreshing menu items...</h3>
              <p className="text-gray-600 text-sm">Please wait while we update the data</p>
            </div>
          </div>
        ) : (
          <MenuList
            items={filteredItems}
            allItems={menuItems}
            viewMode={viewMode}
            selectedItems={selectedItems}
            onSelectItems={setSelectedItems}
            onEdit={canEditItem ? handleEditItem : null}
            onDelete={canDeleteItem ? handleDeleteItem : null}
            categories={branchCategories}
            selectedCategory={selectedCategory}
            onCategoryChange={setSelectedCategory}
            searchQuery={searchQuery}
          />
        )}

        {/* Empty state */}
        {!loading && filteredItems.length === 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
            <div className="text-gray-400 mb-3"><Search size={48} className="mx-auto" /></div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No items found</h3>
            <p className="text-gray-600 text-sm mb-4">Try adjusting your filters</p>
            <button onClick={() => { setSearchQuery(''); setSelectedCategory('all'); setStatusFilter('all'); }} className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 text-sm font-medium">
              Clear Filters
            </button>
          </div>
        )}

        {/* Modals */}
        {showMenuForm && (canAddItem || canEditItem) && (
          <MenuForm
            item={editingItem}
            categories={categories}
            onSave={handleSaveItem}
            serverError={formServerError}
            onClearServerError={() => setFormServerError(null)}
            // Branch-scoped, matching the server's own duplicate check (which runs
            // under the tenantFilter), so the form can catch a duplicate name before
            // the round-trip. The API stays the authority.
            existingItems={itemsInScope}
            onClose={() => { setShowMenuForm(false); setEditingItem(null); setFormServerError(null); }}
            onImageUpload={async (itemId) => {
              await fetchMenuItems();
              const updatedItem = menuItems.find((i) => i.id === itemId);
              if (updatedItem) setEditingItem(updatedItem);
            }}
            onCategoryCreated={fetchCategories}
          />
        )}

        {showCategoryManager && canManageCats && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-teal-100 rounded-lg"><Tag className="w-5 h-5 text-teal-600" /></div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">Manage Categories</h2>
                    <p className="text-xs text-gray-500">Organize menu items with tax configuration</p>
                  </div>
                </div>
                <button onClick={() => { setShowCategoryManager(false); fetchCategories(); }} className="p-2 hover:bg-gray-200 rounded-lg transition-colors">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              <div className="flex-1 overflow-auto">
                <CategoryView branchId={selectedBranchId} />
              </div>
            </div>
          </div>
        )}

        {showBulkUploadDialog && canAddItem && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-100 rounded-lg"><Upload className="w-5 h-5 text-indigo-600" /></div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">Bulk Upload Menu</h2>
                    <p className="text-xs text-gray-500">Upload menu items from an Excel file</p>
                  </div>
                </div>
                <button onClick={() => { setShowBulkUploadDialog(false); setBulkFile(null); }} className="p-2 hover:bg-gray-200 rounded-lg transition-colors">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              
              <div className="p-6 flex flex-col gap-6">
                {/* Version Selector */}
                <div className="border-b pb-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Select Template Version</p>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => { setBulkVersion('V1'); setBulkFile(null); }}
                      className={`px-3 py-2.5 rounded-lg border-2 text-sm font-medium transition-all text-left ${
                        bulkVersion === 'V1'
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <span className="block font-semibold">V1 — Detailed</span>
                      <span className="block text-[11px] mt-0.5 opacity-75">Multi-sheet with all fields</span>
                    </button>
                    <button
                      onClick={() => { setBulkVersion('V2'); setBulkFile(null); }}
                      className={`px-3 py-2.5 rounded-lg border-2 text-sm font-medium transition-all text-left ${
                        bulkVersion === 'V2'
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <span className="block font-semibold">V2 — Simple</span>
                      <span className="block text-[11px] mt-0.5 opacity-75">Single sheet, main fields only</span>
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between border-b pb-4">
                  <div>
                    <p className="text-sm font-medium text-gray-700">1. Download Template</p>
                    <p className="text-xs text-gray-500 mt-1">Get the exact format needed for upload</p>
                  </div>
                  <button 
                    onClick={handleDownloadTemplate} 
                    className="px-3 py-1.5 bg-gray-100 text-gray-700 border border-gray-300 rounded-lg text-sm font-medium flex items-center gap-2 hover:bg-gray-200 transition-colors"
                  >
                    <Download size={16} />
                    <span>Download</span>
                  </button>
                </div>
                
                <div className="flex flex-col gap-2">
                  <div>
                    <p className="text-sm font-medium text-gray-700">2. Upload Filled Template</p>
                    <p className="text-xs text-gray-500 mt-1">Select your completed Excel file to import items</p>
                  </div>
                  <div className="mt-2">
                    <input
                      type="file"
                      accept=".xlsx, .xls"
                      onChange={(e) => setBulkFile(e.target.files[0])}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2.5 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 border border-gray-300 rounded-lg cursor-pointer"
                    />
                  </div>
                </div>
              </div>
              
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
                <button
                  onClick={() => { setShowBulkUploadDialog(false); setBulkFile(null); }}
                  className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium text-sm transition-colors"
                  disabled={isUploading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkUpload}
                  disabled={isUploading || !bulkFile}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm flex items-center gap-2 transition-colors"
                >
                  {isUploading ? <RefreshCw size={16} className="animate-spin" /> : <Upload size={16} />}
                  <span>{isUploading ? 'Uploading...' : 'Upload Data'}</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {showKitchenStationManager && canManageKitchen && (
          <KitchenStationManager
            branchId={selectedBranchId}
            onSave={(stations) => { handleKitchenStationsSave(stations); setShowKitchenStationManager(false); }}
            onClose={() => setShowKitchenStationManager(false)}
          />
        )}

        {confirmConfig && (
          <ConfirmDialog
            isOpen={confirmDialog.isOpen}
            onClose={() => setConfirmDialog({ isOpen: false, type: '', data: null })}
            {...confirmConfig}
          />
        )}

        {bulkUploadResult && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[60] animate-fadeIn">
            <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden animate-scaleIn border border-slate-200">
              {/* Header */}
              <div className={`px-6 py-4 border-b flex items-center justify-between ${bulkUploadResult.status === 'success' ? 'bg-emerald-50/50' : 'bg-red-50/50'}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${bulkUploadResult.status === 'success' ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-600'}`}>
                    {bulkUploadResult.status === 'success' ? <CheckCircle2 size={24} /> : <AlertCircle size={24} />}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 leading-tight">Bulk Upload Results</h3>
                    <p className={`text-xs font-medium ${bulkUploadResult.status === 'success' ? 'text-emerald-600' : 'text-red-600'}`}>
                      {bulkUploadResult.message || (bulkUploadResult.status === 'success' ? 'Process completed successfully' : 'Processing failed')}
                    </p>
                  </div>
                </div>
                <button 
                  onClick={() => setBulkUploadResult(null)}
                  className="p-2 hover:bg-white/80 rounded-lg text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[70vh]">
                <div className="space-y-6">
                  {/* Summary Grid */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex items-center gap-4">
                      <div className="w-10 h-10 bg-indigo-100 text-indigo-600 rounded-lg flex items-center justify-center shrink-0 shadow-sm">
                        <Activity size={20} />
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-0.5">Stations</p>
                        <p className="text-xl font-black text-slate-900 leading-none">{bulkUploadResult.stationsProcessed || 0}</p>
                      </div>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex items-center gap-4">
                      <div className="w-10 h-10 bg-emerald-100 text-emerald-600 rounded-lg flex items-center justify-center shrink-0 shadow-sm">
                        <Tag size={20} />
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-0.5">Categories</p>
                        <p className="text-xl font-black text-slate-900 leading-none">{bulkUploadResult.categoriesProcessed || 0}</p>
                      </div>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex items-center gap-4">
                      <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded-lg flex items-center justify-center shrink-0 shadow-sm">
                        <UtensilsCrossed size={20} />
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-0.5">Items</p>
                        <p className="text-xl font-black text-slate-900 leading-none">{bulkUploadResult.itemsProcessed || 0}</p>
                      </div>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-xl border border-slate-100 flex items-center gap-4">
                      <div className="w-10 h-10 bg-amber-100 text-amber-600 rounded-lg flex items-center justify-center shrink-0 shadow-sm">
                        <Grid size={20} />
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mb-0.5">Variants</p>
                        <p className="text-xl font-black text-slate-900 leading-none">{bulkUploadResult.variantsProcessed || 0}</p>
                      </div>
                    </div>
                  </div>

                  {/* Execution Time */}
                  <div className="flex items-center justify-center gap-2 text-xs text-slate-500 bg-slate-50 py-2 rounded-lg border border-slate-100 border-dashed">
                    <Clock size={14} className="text-slate-400" />
                    <span>Processing completed in <span className="font-bold text-slate-700">{bulkUploadResult.timeTakenMs || 0}ms</span></span>
                  </div>

                  {/* Errors Table */}
                  {bulkUploadResult.rowErrors && bulkUploadResult.rowErrors.length > 0 && (
                    <div className="mt-4">
                      <p className="text-xs font-extrabold text-red-600 mb-2 flex items-center gap-2 uppercase tracking-tight">
                        <AlertCircle size={14} />
                        Row Processing Errors ({bulkUploadResult.rowErrors.length})
                      </p>
                      <div className="bg-white rounded-xl border border-red-100 overflow-hidden shadow-sm">
                        <div className="max-h-60 overflow-y-auto">
                          <table className="w-full text-xs text-left">
                            <thead className="bg-red-50 text-red-700 font-bold sticky top-0">
                              <tr>
                                <th className="px-4 py-2.5 border-b border-red-100 w-20">Row</th>
                                <th className="px-4 py-2.5 border-b border-red-100">Issue Description</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-red-50">
                              {bulkUploadResult.rowErrors.map((err, idx) => (
                                <tr key={idx} className="hover:bg-red-50/40 transition-colors">
                                  <td className="px-4 py-3 font-mono font-bold text-red-500 align-top">{err.rowNumber || idx + 1}</td>
                                  <td className="px-4 py-3 text-slate-600 leading-relaxed font-medium">{err.errorMessage}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 bg-slate-50 border-t flex justify-end">
                <button
                  onClick={() => setBulkUploadResult(null)}
                  className="px-8 py-2.5 bg-slate-900 text-white rounded-xl hover:bg-slate-800 transition-all font-bold text-sm shadow-md hover:shadow-lg active:scale-95"
                >
                  Close Summary
                </button>
              </div>
            </div>
          </div>
        )}
        <AlertDialog
          isOpen={alertDialog.isOpen}
          onClose={() => setAlertDialog({ isOpen: false, title: '', message: '', type: 'error' })}
          title={alertDialog.title}
          message={alertDialog.message}
          type={alertDialog.type}
        />
      </div>
    </div>
  );
}